import queryString from 'query-string';
import React, { ReactNode, useEffect, useState } from 'react';
import ClipboardButton from 'react-clipboard.js';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';
import { ColorProps, SpaceProps } from 'styled-system';

import { ShortUrlType, getShortUrl } from 'api/website';
import Box from 'components/Box';
import { colors, transition } from 'theme';
import { trackClicked } from 'utils/analytics';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants';
import { entries, updateObject } from 'utils/functional';

// {a: 1, b: 2} => [{name: 'a', value: 1}, {name: 'b', value: 2}]
const makeParameters = (params: Record<string, string>) =>
  entries(params).map(([key, value]) => ({
    name: key,
    value,
  }));

const socialLink = (socialType, link, message, listingType) => {
  switch (socialType) {
    case 'facebook': {
      const qs = queryString.stringify(
        updateObject(
          {
            u: link,
          },
          message
            ? {
                title: message,
              }
            : {}
        )
      );
      return `https://facebook.com/sharer/sharer.php?${qs}`;
    }

    case 'twitter': {
      const qs = queryString.stringify({
        text: message,
        url: link,
      });
      return `https://twitter.com/intent/tweet/?${qs}`;
    }

    case 'email': {
      const qs = queryString.stringify({
        subject: message,
        body: `${
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line no-nested-ternary
          listingType === 'CMS_PAGE'
            ? getText('Click the link below to see the article on Idealist:') // TODO: Fix this the next time the file is edited.
            : // eslint-disable-next-line no-nested-ternary
            listingType === 'URL'
            ? getText('Click the link below to see the article from Idealist:')
            : listingType
            ? sprintf(
                getText(
                  'Click the link below to see the %(type)s listing on Idealist:'
                ),
                {
                  type: LISTING_TYPE_HUMAN_NAME_MAP[listingType],
                }
              )
            : getText('Click the link below to see the page on Idealist:')
        }\n\n${link}`,
      });
      return `mailto:?${qs}`;
    }

    case 'linkedin': {
      const qs = queryString.stringify({
        url: link,
        mini: true,
        title: message,
      });
      return `https://www.linkedin.com/shareArticle?${qs}`;
    }

    default:
      throw new Error(`Invalid social type ${socialType}`);
  }
};

const withSocialUtm = (params: Record<string, string>, socialType: string) => ({
  ...params,
  utm_source: socialType,
  utm_campaign: 'visitorshare',
  utm_medium: 'social',
});

const socialWindowFeatures =
  'scrollbars=yes,menubar=no,width=700,height=600,resizable=yes,toolbar=no,location=no,status=no';
type SocialType = 'facebook' | 'twitter' | 'email' | 'linkedin';
const ShareStyleButton = styled.a`
  border-radius: 3px;
  color: white;
  text-decoration: none;
  &:hover,
  &:focus {
    text-decoration: none;
    color: white;
  }
`;
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const CopyClipboardButton = ({
  object,
  objectType,
  ...props
}: {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  object: Record<string, any>;
  objectType: ShortUrlType;
}) => {
  const [shortUrl, setShortUrl] = useState(null);
  useEffect(() => {
    getShortUrl({
      toType: objectType,
      toId: object.id,
    }).then((url) => setShortUrl(url));
  }, [objectType, object.id]);
  return (
    shortUrl && (
      <ShareStyleButton
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        // @ts-expect-error Fix type
        component={ClipboardButton}
        data-clipboard-text={shortUrl}
      />
    )
  );
};
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const ShareButton = ({
  eventName,
  eventProperties,
  object,
  objectType,
  socialType,
  utmParams,
  message,
  onClick,
  ...props
}: SpaceProps &
  ColorProps & {
    children?: ReactNode;
    eventName: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    eventProperties?: Record<string, any>;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    object: Record<string, any>;
    objectType?: ShortUrlType;
    socialType: SocialType;
    utmParams: Record<string, string>;
    message?: string;
    onClick?: () => void;
  }) => {
  const clickHandler = (e) => {
    e.preventDefault();
    trackClicked(eventName, eventProperties || {});
    let linkPs;

    if (objectType) {
      linkPs = getShortUrl({
        toType: objectType,
        toId: object.id,
        extraParams: makeParameters(withSocialUtm(utmParams, socialType)),
      });
    } else {
      // Only use link shortener for Idealist links
      linkPs = object.url.startsWith('/')
        ? getShortUrl({
            toType: 'URL',
            toUrl: object.url,
            extraParams: makeParameters(withSocialUtm(utmParams, socialType)),
          }) // No shortening for links that don't start with '/'
        : Promise.resolve(object.url);
    }

    linkPs.then((shortResourceLink) => {
      const link = socialLink(
        socialType,
        shortResourceLink,
        message,
        objectType
      );
      window.open(link, '', socialWindowFeatures);
    });

    if (onClick) {
      onClick();
    }
  };

  const url =
    objectType === 'CMS_PAGE' ? object.url : object.url[CURRENT_LOCALE];
  return (
    <Box
      component={ShareStyleButton}
      data-qa-id={`social-button-${socialType}`}
      display="inline-block"
      onClick={clickHandler}
      href={
        object.url
          ? socialLink(
              socialType,
              objectType
                ? `https://idealist.org${url.startsWith('/') ? '' : '/'}${url}`
                : object.url,
              message,
              objectType
            )
          : ''
      }
      target="_blank"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
};
export const SocialButton = styled(ShareButton)`
  display: block;
  margin: 6px;
  border-radius: 50%;
  background: ${colors.platinum};
  color: ${colors.battleshipGrey};
  transition: all ${transition};
  &:hover,
  &:focus {
    background: ${colors.waterBlue};
    color: white;
  }
`;
